import type { Auth0ContextInterface } from '@auth0/auth0-react'

class AuthManager {
	private authContext: Auth0ContextInterface | null = null
	private _accessToken: string | null = null

	setAuthContext(context: Auth0ContextInterface) {
		this.authContext = context
	}

	async getAccessToken(): Promise<string> {
		if (!this.authContext) {
			throw new Error('Auth0 context not initialized')
		}

		try {
			this._accessToken = await this.authContext.getAccessTokenSilently()
			return this._accessToken
		} catch (error) {
			// biome-ignore lint/suspicious/noExplicitAny: the error thrown from auth0 is not typed
			const errorCode = (error as any).error
			if (errorCode === 'invalid_grant' || errorCode === 'login_required') {
				await this.login()
			}
			console.error('Get token error: ', error)
			return ''
		}
	}

	/**
	 * getAccessTokenSync is a workaround to allow attaching access tokens to lightning layer requests.
	 * These requests are initiated by Mapbox, and Mapbox only allows request customization via
	 * transformRequest, which does not support async functions. This approach is safe because a previously
	 * issued access token remains valid even if getAccessToken() would return a newer one after a refresh.
	 * We rely on the cached _accessToken being valid without explicitly triggering a refresh, since most
	 * of our REST API calls use a stale time of 1–5 minutes, well within the planned access token validity
	 * window. The getAccessToken() call here acts as a safety net: it allows user interactions with the map
	 * to opportunistically trigger a token refresh check, in case we later decide to increase the stale time
	 * for API calls.
	 */
	getAccessTokenSync(): string | null {
		this.getAccessToken().catch(() => console.warn('Failed to get access token silently'))
		return this._accessToken
	}

	isAuthenticated(): boolean {
		return this.authContext?.isAuthenticated ?? false
	}

	getAuthError(): Error | null {
		return this.authContext?.error ?? null
	}

	async login({ returnTo }: { returnTo?: string } = {}): Promise<void> {
		if (!this.authContext) {
			throw new Error('Auth0 context not initialized')
		}
		await this.authContext.loginWithRedirect({ appState: { returnTo: returnTo ?? location.pathname + location.search + location.hash } })
	}

	async logout(): Promise<void> {
		if (!this.authContext) {
			throw new Error('Auth0 context not initialized')
		}
		await this.authContext.logout({ logoutParams: { returnTo: location.origin } })
	}
}

export const authManager = new AuthManager()
